import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { GrLinkTop } from 'react-icons/gr';
import { SUMMARIZER_API } from '../apiConfig';
import './Summarizer.css';

function Summarizer() {
  const [inputText, setInputText] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState('paragraph');
  const [length, setLength] = useState('medium');
  const [copyStatus, setCopyStatus] = useState('Copy');
  const [activeFaq, setActiveFaq] = useState(null);

  const [ballCount, setBallCount] = useState(0);
  const ballRefs = useRef([]);

  useEffect(() => {
    const spacingVH = 70;
    const calculateBallCount = () => {
      const pageHeight = document.documentElement.scrollHeight;
      const vh = window.innerHeight / 100;
      const spacingPx = spacingVH * vh;
      const count = Math.ceil(pageHeight / spacingPx);
      setBallCount(count);
    };
    calculateBallCount();
    window.addEventListener('resize', calculateBallCount);
    window.addEventListener('scroll', calculateBallCount);
    const observer = new MutationObserver(calculateBallCount);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener('resize', calculateBallCount);
      window.removeEventListener('scroll', calculateBallCount);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    ballRefs.current.forEach(ball => ball && observer.observe(ball));
    return () => observer.disconnect();
  }, [ballCount]);

  const getInputWordCount = () =>
    inputText.trim() ? inputText.trim().split(/\s+/).length : 0;

  const handleSummarize = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    setSummary('');
    try {
      const response = await fetch(`${SUMMARIZER_API}/api/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: inputText, type: format, length }),
      });
      const data = await response.json();
      if (data.summary) {
        setSummary(data.summary);
      } else if (data.error) {
        setSummary(`Error: ${data.error}`);
      }
    } catch (err) {
      setSummary('Error: Could not connect to the backend. The server may be waking up; please try again in 30 seconds.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInputText('');
    setSummary('');
    setCopyStatus('Copy');
  };

  const handleCopy = () => {
    if (!summary) return;
    navigator.clipboard.writeText(summary).then(() => {
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus('Copy'), 2000);
    });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Free AI Text Summarizer — Summarize Any Text Instantly | OOK Calculator</title>
        <meta
          name="description"
          content="Summarize any text in seconds with OOK Calculator's free AI summarizer. Paste articles, essays, research papers or any content and get a concise, accurate summary instantly. No sign-up needed."
        />
        <meta
          name="keywords"
          content="AI text summarizer, free summarizer tool, summarize text online, article summarizer, paragraph summarizer, AI summary generator, text summary tool, online summarizer, summarize essay, automatic summarizer"
        />
        <meta name="author" content="OOK Calculator" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href="https://ook-calculator.com/Summarizer" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OOK Calculator" />
        <meta property="og:title" content="Free AI Text Summarizer — Summarize Any Text Instantly" />
        <meta property="og:description" content="Summarize articles, essays, research papers and more in seconds. Free AI-powered text summarizer by OOK Calculator — no sign-up required." />
        <meta property="og:url" content="https://ook-calculator.com/Summarizer" />
        <meta property="og:image" content="https://ook-calculator.com/og/summarizer-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@OOKCalculator" />
        <meta name="twitter:title" content="Free AI Text Summarizer — Summarize Any Text Instantly" />
        <meta name="twitter:description" content="Summarize articles, essays and papers in seconds. Free AI summarizer by OOK Calculator — no sign-up required." />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebApplication",
                "@id": "https://ook-calculator.com/Summarizer#app",
                "name": "OOK AI Text Summarizer",
                "description": "A free AI-powered text summarizer that condenses articles, essays, research papers, and any long-form content into concise, accurate summaries instantly. No sign-up required.",
                "url": "https://ook-calculator.com/Summarizer",
                "applicationCategory": "UtilitiesApplication",
                "operatingSystem": "Any (Web Browser)",
                "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
                "featureList": [
                  "AI-powered text summarization",
                  "Supports articles, essays, research papers, blog posts",
                  "Bullet-point and paragraph summary formats",
                  "Adjustable summary length",
                  "No sign-up or account required",
                  "Multi-language support",
                  "Instant results"
                ],
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.8",
                  "reviewCount": "1240",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "@id": "https://ook-calculator.com/Summarizer#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "What is an AI text summarizer?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "An AI text summarizer is a tool that uses artificial intelligence and natural language processing (NLP) to automatically read a long piece of text and condense it into a shorter version, preserving the key points and main ideas."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is the OOK Summarizer free to use?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. The OOK AI Text Summarizer is completely freeSimply paste your text and click Summarize."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What types of content can I summarize?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "You can summarize any type of long-form text including news articles, academic research papers, essays, blog posts, reports, books chapters, meeting notes, legal documents, and more."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How accurate is the AI summary?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "The OOK Summarizer uses advanced NLP algorithms to extract the most important sentences and concepts from your text. It maintains the original meaning and context, producing highly accurate summaries suitable for academic and professional use."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can I choose the length of the summary?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. You can adjust the summary length to get a shorter or longer summary depending on your needs. You can also choose between paragraph format or bullet-point format."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is my text data stored or saved?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "No. OOK Calculator does not store or save any text you paste into the summarizer. Your data is processed in real time and discarded immediately after the summary is generated, ensuring your privacy."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://ook-calculator.com/Summarizer#breadcrumb",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ook-calculator.com" },
                  { "@type": "ListItem", "position": 2, "name": "AI Writing Tools", "item": "https://ook-calculator.com/ai-tools" },
                  { "@type": "ListItem", "position": 3, "name": "AI Text Summarizer", "item": "https://ook-calculator.com/Summarizer" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="summarizer-wrapper">
        {/* BREADCRUMB */}
        <nav className="smr-breadcrumb" aria-label="Breadcrumb">
          <a href="https://ook-calculator.com">Home</a>
          <span aria-hidden="true">›</span>
          <a href="https://ook-calculator.com/ai-tools">AI Writing Tools</a>
          <span aria-hidden="true">›</span>
          <span aria-current="page">AI Text Summarizer</span>
        </nav>

        {/* HERO */}
        <header className="smr-hero" role="banner">
          <div className="smr-badge" aria-label="Tool category">Free Online Tool · No Sign-up</div>
          <h1>
            Free <em>AI Text Summarizer</em> —<br />
            <strong>Condense Any Text Instantly</strong>
          </h1>
          <p className="smr-hero-sub">
            Paste any article, essay, research paper, or document and get a clear, concise AI-generated summary in seconds. Powered by natural language processing (NLP).
          </p>
        </header>

        {/* BACKGROUND BALL ANIMATION */}
        <div className="smr-ball-section">
          {Array.from({ length: ballCount }, (_, i) => (
            <div
              key={i}
              ref={el => (ballRefs.current[i] = el)}
              className={`smr-ball smr-ball-${(i % 10) + 1}`}
              style={{
                position: 'absolute',
                top: `${i * 70}vh`,
                left: i % 2 === 0 ? '0%' : '97%',
                transform: 'translateX(-50%)',
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* TOOL WIDGET */}
        <main id="main-content">
          <section className="smr-tool-section" aria-label="AI Text Summarizer tool">
            {/* FORMAT TABS */}
            <div className="smr-format-tabs" role="tablist" aria-label="Summary format">
              {[
                { id: 'paragraph', label: '¶ Paragraph' },
                { id: 'bullets', label: '• Bullet Points' },
              ].map(t => (
                <button
                  key={t.id}
                  className={`smr-tab ${format === t.id ? 'active' : ''}`}
                  role="tab"
                  aria-selected={format === t.id}
                  onClick={() => setFormat(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="smr-editor-grid">
              {/* INPUT PANE */}
              <div className="smr-editor-pane">
                <div className="smr-pane-header">
                  <span><span className="smr-icon">✏</span>&nbsp;INPUT</span>
                  <span className="smr-word-count" id="smr-wc">
                    {getInputWordCount()} words
                  </span>
                </div>
                <textarea
                  id="smr-inputText"
                  placeholder="Paste any article, essay, research paper, blog post, report, or document here and click Summarize..."
                  aria-label="Text to summarize"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                />
                <div className="smr-pane-footer">
                  <div className="smr-length-group">
                    <span className="smr-length-label">Length:</span>
                    {['short', 'medium', 'long'].map(l => (
                      <button
                        key={l}
                        className={`smr-length-btn ${length === l ? 'active' : ''}`}
                        onClick={() => setLength(l)}
                      >
                        {l.charAt(0).toUpperCase() + l.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="smr-action-btns">
                    <button className="smr-btn-sm" onClick={handleReset}>Clear</button>
                    <button
                      className="smr-btn-sm primary"
                      id="smr-summarizeBtn"
                      onClick={handleSummarize}
                      disabled={loading || !inputText.trim()}
                    >
                      {loading ? <span className="smr-loading-spinner"></span> : '▶ Summarize'}
                    </button>
                  </div>
                </div>
              </div>

              {/* OUTPUT PANE */}
              <div className="smr-editor-pane">
                <div className="smr-pane-header">
                  <span><span className="smr-icon">⚡</span>&nbsp;SUMMARY</span>
                  <span className="smr-word-count">
                    {summary ? `${summary.trim().split(/\s+/).length} words` : '—'}
                  </span>
                </div>
                <div className="smr-output-area" id="smr-outputArea" aria-live="polite" role="region" aria-label="Summary result">
                  {!summary && !loading ? (
                    <div className="smr-standby" id="smr-standbyMsg">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                        <rect x="4" y="11" width="3" height="6" rx="1.5" fill="#ccc" />
                        <rect x="9.5" y="7" width="3" height="14" rx="1.5" fill="#ccc" />
                        <rect x="15" y="9" width="3" height="10" rx="1.5" fill="#ccc" />
                        <rect x="20.5" y="12" width="3" height="4" rx="1.5" fill="#ccc" />
                      </svg>
                      Neural engine on standby.<br />Paste text and click Summarize.
                    </div>
                  ) : (
                    summary
                  )}
                </div>
                <div className="smr-pane-footer">
                  <span></span>
                  <div className="smr-pane-actions">
                    <button
                      className="smr-btn-sm"
                      id="smr-copyBtn"
                      onClick={handleCopy}
                      disabled={!summary || loading}
                      aria-label="Copy summary to clipboard"
                    >
                      {copyStatus}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <p className="smr-tool-disclaimer">
              Free tool &nbsp;·&nbsp; Zero data retention &nbsp;·&nbsp; Supports multiple languages
            </p>
          </section>

          {/* STATS */}
          <div className="smr-stats-grid" role="list" aria-label="Usage statistics">
            <div className="smr-stat-cell" role="listitem">
              <div className="smr-stat-num">2M+</div>
              <div className="smr-stat-label">Texts Summarized</div>
            </div>
            <div className="smr-stat-cell" role="listitem">
              <div className="smr-stat-num">4.8★</div>
              <div className="smr-stat-label">User Rating</div>
            </div>
            <div className="smr-stat-cell" role="listitem">
              <div className="smr-stat-num">&lt;5s</div>
              <div className="smr-stat-label">Average Response</div>
            </div>
            {/* <div className="smr-stat-cell" role="listitem">
              <div className="smr-stat-num">100%</div>
              <div className="smr-stat-label">Free Forever</div>
            </div> */}
          </div>

          {/* FEATURES */}
          <section className="smr-content-wrap smr-section" id="features" aria-labelledby="features-heading">
            <p className="smr-section-eyebrow">Why use OOK Summarizer</p>
            <h2 id="features-heading" className="smr-section-title">Everything you need in<br /><em>a text summarizer</em></h2>
            <div className="smr-features-grid" role="list">
              <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" /></svg>
                </div>
                <div>
                  <h4>Any content type</h4>
                  <p>Summarize news articles, research papers, academic essays, blog posts, legal documents, meeting notes, and more.</p>
                </div>
              </article>
              <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
                </div>
                <div>
                  <h4>Instant results</h4>
                  <p>Get your summary in under 5 seconds. No loading screens, no queues — just fast, accurate AI summarization.</p>
                </div>
              </article>
              <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h7" /></svg>
                </div>
                <div>
                  <h4>Flexible output formats</h4>
                  <p>Choose between paragraph summaries for flowing prose, or bullet points for scannable, structured output.</p>
                </div>
              </article>
              <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>
                </div>
                <div>
                  <h4>Adjustable length</h4>
                  <p>Control how short or long your summary is with a single click. Perfect for social media snippets or detailed overviews.</p>
                </div>
              </article>
              {/* <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15 15 0 0 1 0 20" /></svg>
                </div>
                <div>
                  <h4>Multi-language support</h4>
                  <p>Summarize text in English, Spanish, French, German, Hindi, Arabic, Portuguese, and many more languages.</p>
                </div>
              </article> */}
              <article className="smr-feature-card" role="listitem">
                <div className="smr-feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                </div>
                <div>
                  <h4>100% private</h4>
                  <p>Your text is never saved or stored. It's processed in real time and discarded immediately — your data stays yours.</p>
                </div>
              </article>
            </div>
          </section>

          {/* SEO CONTENT — What is it */}
          <section className="smr-content-wrap smr-section" aria-labelledby="about-heading">
            <p className="smr-section-eyebrow">About This Tool</p>
            <h2 id="about-heading" className="smr-section-title">What is an <em>AI text summarizer?</em></h2>
            <div className="smr-prose">
              <p>An <strong>AI text summarizer</strong> is an online tool that uses artificial intelligence — specifically natural language processing (NLP) and machine learning techniques — to read a long piece of text and automatically produce a shorter version that preserves the essential meaning, key arguments, and most important information. Instead of reading a 3,000-word research paper, you can paste it into a summarizer and receive a 200-word overview in seconds.</p>
              <p>The OOK AI Summarizer uses advanced NLP algorithms to analyse your text, identify the most significant sentences, score them by relevance and context, and reconstruct a coherent, concise summary. It does this in seconds, making it one of the fastest free summarizing tools available online.</p>
            </div>
          </section>

          {/* HOW TO USE */}
          <section className="smr-content-wrap smr-section" aria-labelledby="howto-heading">
            <p className="smr-section-eyebrow">Quick Start</p>
            <h2 id="howto-heading" className="smr-section-title">How to use the <em>OOK Summarizer</em></h2>
            <div className="smr-how-steps" role="list">
              {[
                { n: '01', title: 'Paste your text', body: 'Copy any article, essay, research paper, blog post, or document and paste it into the input box. There is no character minimum, but longer texts (200+ words) produce the best summaries.' },
                { n: '02', title: 'Choose your format and length', body: 'Select whether you want your summary as a flowing paragraph or a bullet-point list. Then choose a length — short, medium, or long — depending on how much detail you need.' },
                { n: '03', title: 'Click Summarize', body: 'Hit the Summarize button. The AI processes your text and generates a concise, accurate summary in under 5 seconds. No sign-up, no CAPTCHA, no waiting.' },
                { n: '04', title: 'Copy your summary', body: 'Copy your summary to the clipboard with one click. Use it in your notes, presentation, email, or social media post.' },
              ].map(step => (
                <div className="smr-how-step" role="listitem" key={step.n}>
                  <span className="smr-step-n">{step.n}</span>
                  <div className="smr-step-body">
                    <h4>{step.title}</h4>
                    <p>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* USE CASES */}
          <section className="smr-content-wrap smr-section" aria-labelledby="usecases-heading">
            <p className="smr-section-eyebrow">Use Cases</p>
            <h2 id="usecases-heading" className="smr-section-title">Who should use a <em>text summarizer?</em></h2>
            <div className="smr-use-grid" role="list">
              {[
                { tag: 'Students', title: 'Academic research', body: 'Summarize dense academic papers, textbook chapters, and journal articles quickly. Save hours of reading time and extract key arguments for your assignments.' },
                { tag: 'Writers & Bloggers', title: 'Content creation', body: 'Condense long research articles into concise reference notes. Create social media snippets from long-form blog posts. Repurpose content efficiently.' },
                { tag: 'Professionals', title: 'Business & legal', body: 'Quickly digest lengthy business reports, contracts, meeting transcripts, and industry news without reading every word. Stay informed faster.' },
                { tag: 'Researchers', title: 'Literature review', body: 'Process large volumes of research papers during a literature review. Extract key findings and methodology from abstracts and full papers rapidly.' },
              ].map(uc => (
                <div className="smr-use-cell" role="listitem" key={uc.tag}>
                  <div className="smr-use-tag">{uc.tag}</div>
                  <h4>{uc.title}</h4>
                  <p>{uc.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* HOW AI WORKS */}
          <section className="smr-content-wrap smr-section" aria-labelledby="how-ai-heading">
            <p className="smr-section-eyebrow">Under the Hood</p>
            <h2 id="how-ai-heading" className="smr-section-title">How does AI text <em>summarization work?</em></h2>
            <div className="smr-prose">
              <p>Modern AI text summarization relies on <strong>natural language processing (NLP)</strong> and <strong>machine learning</strong>. There are two main approaches:</p>
              <ul>
                <li><strong>Extractive summarization</strong> — The algorithm identifies and extracts the most important existing sentences from the original text without changing them. It scores each sentence based on keyword frequency, position, and relevance to the document's main topic.</li>
                <li><strong>Abstractive summarization</strong> — More advanced models generate entirely new sentences that paraphrase and condense the original ideas, similar to how a human would write a summary. This requires understanding the deeper meaning and context of the text.</li>
              </ul>
              <p>The OOK Summarizer combines both techniques to deliver summaries that are both accurate and naturally readable.</p>
            </div>

            <h3 className="smr-subheading">Tips for getting the best summaries</h3>
            <ol className="smr-tips-list">
              <li><strong>Provide clean text.</strong> Remove navigation menus, ads, or irrelevant content before pasting. Plain article text works best.</li>
              <li><strong>Use well-structured content.</strong> Text with clear paragraphs, headings, and topic sentences produces more coherent summaries.</li>
              <li><strong>Adjust the length.</strong> For technical content, use a longer summary to preserve important details. For quick reads, a short summary works well.</li>
              <li><strong>Try bullet points for lists.</strong> If the original text is structured as a list of points, the bullet-point output format is often clearer.</li>
              <li><strong>Summarize section by section.</strong> For very long documents (books, long reports), summarize chapter by chapter for better accuracy.</li>
            </ol>
          </section>

          {/* FAQ */}
          <section className="smr-content-wrap smr-section" id="faq" aria-labelledby="faq-heading">
            <p className="smr-section-eyebrow">FAQ</p>
            <h2 id="faq-heading" className="smr-section-title">Frequently asked <em>questions</em></h2>
            <div className="smr-faq-list">
              {[
                {
                  q: 'What is an AI text summarizer?',
                  a: 'An AI text summarizer is a tool that uses artificial intelligence and natural language processing (NLP) to automatically read a long piece of text and condense it into a shorter version, preserving the key points and main ideas without requiring any manual effort.',
                },
                {
                  q: 'Is the OOK Summarizer free to use?',
                  a: 'Yes. The OOK AI Text Summarizer is completely free to use. Simply paste your text and click Summarize to get an instant summary.',
                },
                {
                  q: 'What types of content can I summarize?',
                  a: 'You can summarize any type of long-form text including news articles, academic research papers, essays, blog posts, business reports, meeting notes, legal documents, book chapters, emails, and more. The tool works best with structured text of 200 words or more.',
                },
                {
                  q: 'Is my text data private and secure?',
                  a: 'Yes. OOK Calculator does not store, log, or save any text you submit to the summarizer. Your content is processed in real time and discarded immediately after the summary is generated. We do not share your data with any third parties.',
                },
                {
                  q: 'How accurate is the AI summary?',
                  a: 'The OOK Summarizer uses advanced NLP algorithms trained on diverse text datasets to extract and condense the most important information from your text. It maintains the original meaning and context, producing summaries that are suitable for academic, professional, and personal use.',
                },
                {
                  q: 'Can I choose the length of the summary?',
                  a: 'Yes. You can select short, medium, or long summaries using the length selector. You can also switch between paragraph format and bullet-point format depending on your use case.',
                },
              ].map((item, i) => (
                <details key={i} className="smr-faq-item" open={activeFaq === i} onClick={(e) => {
                  e.preventDefault();
                  setActiveFaq(activeFaq === i ? null : i);
                }}>
                  <summary>{item.q}</summary>
                  <div className="smr-faq-answer">{item.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* RELATED TOOLS */}
          <section className="smr-content-wrap smr-section" aria-labelledby="related-heading">
            <p className="smr-section-eyebrow">Explore More Free Writing Tools</p>
            <h2 id="related-heading" className="smr-section-title">Explore more <em>free writing tools</em></h2>
            <nav className="smr-related-grid" aria-label="Related tools">
              <a href="https://ook-calculator.com/Paraphraser" className="smr-related-card" aria-label="AI Paraphraser tool">
                <span aria-hidden="true">✏️</span>
                <span>AI Paraphraser</span>
              </a>
              <a href="https://ook-calculator.com/PlagiarismChecker" className="smr-related-card" aria-label="Plagiarism Checker">
                <span aria-hidden="true">📄</span>
                <span>Plagiarism Checker</span>
              </a>
              <a href="https://ook-calculator.com/AIDetector" className="smr-related-card" aria-label="AI Content Detector">
                <span aria-hidden="true">🤖</span>
                <span>AI Content Detector</span>
              </a>
              <a href="https://ook-calculator.com/Humanizer" className="smr-related-card" aria-label="AI Humanizer">
                <span aria-hidden="true">🧑</span>
                <span>AI Humanizer</span>
              </a>
              <a href="https://ook-calculator.com/GrammarChecker" className="smr-related-card" aria-label="Grammar Checker">
                <span aria-hidden="true">📝</span>
                <span>Grammar Checker</span>
              </a>
              <a href="https://ook-calculator.com" className="smr-related-card" aria-label="All Tools">
                <span aria-hidden="true">🧮</span>
                <span>All Tools</span>
              </a>
            </nav>
          </section>
        </main>

        {/* SCROLL TO TOP */}
        <section className="smr-scroll-top">
          <Link to="#" onClick={scrollToTop} aria-label="Scroll to top">
            <GrLinkTop />
          </Link>
        </section>
      </div>
    </>
  );
}

export default Summarizer;
